// Warm, friendly guided-tour + Help Center content for Two Degrees (web).
// Tours highlight the REAL, live UI, so previews always show real content,
// never test data or placeholder names.

export const MEMBER_WELCOME_KEY = "member_welcome";
export const BUSINESS_WELCOME_KEY = "business_welcome";

// Each step: { title, body, targets?: string[], route?: string }
// `targets` are data-testid values, the overlay spotlights the first visible one.
// `route` (member tours) navigates there before showing the step.
export const TOURS = {
  [MEMBER_WELCOME_KEY]: {
    title: "Welcome tour",
    steps: [
      {
        title: "Welcome to Two Degrees!",
        body: "So happy you're here. Let's take a quick, friendly spin around, 60 seconds, promise. You can skip anytime and replay it later from Help.",
      },
      {
        title: "Your Home base",
        body: "This is where it all comes together, events near you, people worth meeting, and fresh conversations. It gently updates as your community grows.",
        targets: ["nav-home", "tab-home"],
        route: "/",
      },
      {
        title: "Discover Events",
        body: "Browse gatherings happening around you. RSVP in a tap, grab a ticket, and you'll get a QR pass to breeze in at the door.",
        targets: ["nav-events", "tab-events"],
        route: "/events",
      },
      {
        title: "Join the Community",
        body: "Share a win, ask a question, or drop an intro. This is the warm, buzzing heart of Two Degrees, jump into any thread.",
        targets: ["nav-community", "tab-community"],
        route: "/community",
      },
      {
        title: "Grow your Network",
        body: "Everyone here is two degrees away. Send a connection, tag how you know them, and let us suggest warm intros to the right people.",
        targets: ["nav-members", "tab-connections"],
        route: "/connections",
      },
      {
        title: "Messages",
        body: "Keep the conversation going. Chat one-to-one, share a photo or file, and pick up right where you left off.",
        targets: ["nav-messages", "topbar-messages"],
        route: "/messages",
      },
      {
        title: "You're all set!",
        body: "That's the whistle-stop tour. Anytime you want a refresher or a guide to a specific feature, open the Help Center. Go make some great connections!",
        targets: ["nav-help", "topbar-help"],
        route: "/help",
      },
    ],
  },

  feat_getting_started: {
    title: "Getting started",
    steps: [
      {
        title: "Find your way around",
        body: "Your main areas live in the menu: Home, Events, Community, Network and Messages. Tap any of them to explore.",
        targets: ["nav-home", "tab-home"],
        route: "/",
      },
      {
        title: "Make it yours",
        body: "Head to My 2° to polish your profile, add a photo, and tell people what you're looking for, it helps us suggest better matches.",
        targets: ["nav-me", "tab-me"],
        route: "/me",
      },
    ],
  },
  feat_events: {
    title: "Events",
    steps: [
      {
        title: "Browse & RSVP",
        body: "Tap any event to see the details, then RSVP or grab a ticket. Free events add a QR pass to your Tickets instantly.",
        targets: ["nav-events", "tab-events"],
        route: "/events",
      },
    ],
  },
  feat_community: {
    title: "Community",
    steps: [
      {
        title: "Start a conversation",
        body: "Post a thought, reply to others, and @mention people to bring them in. Little interactions here spark real connections.",
        targets: ["nav-community", "tab-community"],
        route: "/community",
      },
    ],
  },
  feat_network: {
    title: "Networking & connections",
    steps: [
      {
        title: "Connect with purpose",
        body: "See who's in your circle, accept requests, and tag connections so you remember how you met. We'll even suggest warm intros.",
        targets: ["nav-members", "tab-connections"],
        route: "/connections",
      },
    ],
  },
  feat_messages: {
    title: "Messaging",
    steps: [
      {
        title: "Chat anytime",
        body: "Open Messages to DM anyone, share photos and files, and react with emoji. Edit or delete a message you sent by hovering it on web or long-pressing on mobile, then choosing Edit or Delete; edited messages show a small \"edited\" label. Unread chats show a little dot so nothing slips by.",
        targets: ["nav-messages", "topbar-messages"],
        route: "/messages",
      },
    ],
  },
  feat_wallet: {
    title: "Two Degrees Pay",
    steps: [
      {
        title: "Load & pay",
        body: "Add a balance to your wallet, then show your rotating QR to pay at events or settle an invoice. Turn on auto-reload so you're never caught short.",
        targets: ["nav-wallet"],
        route: "/wallet",
      },
    ],
  },
  feat_profile: {
    title: "Profile & verification",
    steps: [
      {
        title: "Your My 2° page",
        body: "This is your home for everything you. Edit your profile, share your QR to connect in person, and request verification for that trusted badge.",
        targets: ["nav-me", "tab-me"],
        route: "/me",
      },
    ],
  },
  feat_jobs: {
    title: "Opportunities",
    steps: [
      {
        title: "Opportunities board",
        body: "Browse roles and gigs from the community (this used to be called Jobs). Tap one for the full details and apply in a couple of taps, your profile does the intro.",
        targets: ["nav-jobs", "tab-home"],
        route: "/jobs",
      },
    ],
  },
  feat_social: {
    title: "Social Media Hub",
    steps: [
      {
        title: "Plan & schedule posts",
        body: "Connect your channels, compose once, and schedule across platforms. We even suggest the best time to post, drawn from your recent engagement.",
        targets: ["nav-social"],
        route: "/social",
      },
    ],
  },
  feat_learning: {
    title: "Learning",
    steps: [
      {
        title: "Grow with courses",
        body: "Browse courses and work through lessons at your own pace, your progress saves as you go.",
        targets: ["nav-learning"],
        route: "/learn",
      },
    ],
  },
  feat_teamchat: {
    title: "Team Chat",
    steps: [
      {
        title: "Chat with your team",
        body: "Create channels, send direct messages, invite guests, and keep every conversation in one organized place.",
        route: "/team-chat",
      },
    ],
  },

  // ----- Business tours (run inside the Business Console) -----
  [BUSINESS_WELCOME_KEY]: {
    title: "Business Dashboard tour",
    mode: "business",
    steps: [
      {
        title: "Welcome to your Business Dashboard",
        body: "This is mission control for your organization on Two Degrees. Let's peek at what you can do, quick and painless.",
      },
      {
        title: "Overview & Analytics",
        body: "Start here for a pulse on everything, RSVPs, check-ins, sales and what needs your attention. Dig deeper anytime in Analytics.",
        targets: ["biz-nav-analytics", "biz-nav-overview"],
      },
      {
        title: "Create & manage Events",
        body: "Publish events, manage attendees, and handle check-ins right here. Tap “New event” up top whenever inspiration strikes.",
        targets: ["biz-nav-events"],
      },
      {
        title: "POS & Invoices",
        body: "Build a bill of sale from your catalog, apply tax, and share a QR for members to pay on the spot, or send an invoice straight to them.",
        targets: ["biz-nav-pos"],
      },
      {
        title: "Take payments & scan",
        body: "Under Payments you can scan a member's pay code to charge them, and see everything you're owed at a glance.",
        targets: ["biz-nav-payments"],
      },
      {
        title: "Your Portfolio",
        body: "Showcase your work with documents and images so members get the full picture of who you are.",
        targets: ["biz-nav-portfolio"],
      },
      {
        title: "Business tools & team access",
        body: "Projects, Docs, HR, Payroll, Recruiting, Accounting, Expenses and more run privately for your business. Under Tools / Setup, grant each teammate access to just the tools they need — owners and managers always have everything.",
        targets: ["biz-nav-setup"],
      },
      {
        title: "You're ready to roll!",
        body: "That's your dashboard in a nutshell. Reach for the Help tab whenever you'd like a walkthrough of any area. Have fun building!",
        targets: ["biz-nav-help"],
      },
    ],
  },
  feat_biz_events: {
    title: "Creating events",
    mode: "business",
    steps: [
      {
        title: "Publish an event",
        body: "Open Events, then tap “New event” to add the details, a cover image and tickets. You can manage attendees and check people in from the same place.",
        targets: ["biz-nav-events"],
      },
    ],
  },
  feat_biz_pos: {
    title: "Invoicing & POS",
    mode: "business",
    steps: [
      {
        title: "Build an invoice",
        body: "In POS / Invoices, add items from your catalog, apply your tax rate, and generate a pay QR, or send the invoice directly to a member.",
        targets: ["biz-nav-pos"],
      },
    ],
  },
  feat_biz_scan: {
    title: "QR scanning & payments",
    mode: "business",
    steps: [
      {
        title: "Scan to charge",
        body: "Head to Payments and scan a member's pay code to take a payment instantly. Recent payments and your balance owed live right here.",
        targets: ["biz-nav-payments"],
      },
    ],
  },
  feat_biz_analytics: {
    title: "Analytics",
    mode: "business",
    steps: [
      {
        title: "Know your numbers",
        body: "Analytics breaks down views, RSVPs, attendance and sales. Tap any stat to see the individual records behind it.",
        targets: ["biz-nav-analytics"],
      },
    ],
  },
  feat_biz_catalog: {
    title: "Product catalog",
    mode: "business",
    steps: [
      {
        title: "Set up your catalog",
        body: "Inside POS / Invoices you can add products and services with prices and tax settings, they'll be ready to drop into any invoice.",
        targets: ["biz-nav-pos"],
      },
    ],
  },
  feat_biz_tools: {
    title: "Business tools & team access",
    mode: "business",
    steps: [
      {
        title: "Assign tools to your team",
        body: "Open Tools / Setup to turn on access per teammate for CRM, Projects, Docs, HR, Payroll, Recruiting, Accounting, Expenses and more. Each tool is private to your business.",
        targets: ["biz-nav-setup"],
      },
    ],
  },
};

// Help Center categories for MEMBERS.
export const HELP_MEMBER = [
  {
    key: "getting-started",
    title: "Getting started",
    blurb: "New here? Start with the basics.",
    topics: [
      { title: "Replay the welcome tour", desc: "The full 60-second spin around the app.", tour: MEMBER_WELCOME_KEY },
      { title: "Finding your way around", desc: "Where everything lives.", tour: "feat_getting_started" },
      {
        id: "ask-ai",
        title: "Emmy (your assistant)",
        desc: "Draft, brainstorm and polish with Emmy.",
        article: [
          "Tap Emmy's button (bottom-right on any page) to chat with her, your Two Degrees assistant powered by Claude. Ask her to draft a message, brainstorm outreach, plan who to meet, or explain anything in the app. She tucks out of the way while you scroll so she never blocks a button.",
          "AI also helps inline: tap \"Write with AI\" next to your bio in Settings to generate a professional bio, and \"Draft with AI\" on an invoice note to write a polite client message.",
        ],
      },
      {
        title: "Welcome back & Show me stay at the top",
        desc: "Your greeting and location filter are pinned.",
        article: [
          "At the top of Home, your \"Welcome back\" greeting and the \"Show me\" location filter now stay fixed in place while the rest of the page scrolls, so you can re-scope your feed at any time without scrolling back up.",
          "Everything below stays fully customizable — tap Customize to choose which sections appear and in what order.",
        ],
      },
      {
        title: "Why the app feels faster now",
        desc: "Screens open instantly and refresh in the background.",
        article: [
          "We've optimized the app so screens appear right away: each page now loads only the code it needs, and screens you've already visited reopen instantly from a local cache while quietly refreshing in the background.",
          "You may briefly see lightweight skeleton placeholders the very first time a screen loads on a slow connection — that's the page getting ready, and real content fills in a moment later.",
        ],
      },
      {
        id: "daily-checkin",
        title: "Daily Check-In",
        desc: "A daily 3-task checklist that earns reward points.",
        article: [
          "The Daily Check-In lives at the top of your Home screen: a short checklist of three quick actions that refreshes every day at midnight (your local time).",
          { heading: "The three tasks", items: [
            "Join the Conversation — answer today's prompt (+5 points).",
            "Share a moment on The Loop — post a photo from your day (+3 points).",
            "See what's happening near you — check out upcoming events in your city (+5 points).",
          ] },
          { heading: "Good to know", items: [
            "You can earn up to 13 reward points a day from the check-in, on top of points you earn elsewhere.",
            "Tap a task to jump straight to it. The Conversation and Loop tasks tick off once you actually post, and Events ticks when you take a look. Completed tasks stay checked until the next day.",
            "Not into it today? Tap the × to close the check-in — it comes back fresh tomorrow.",
            "Each week you may see a Member Spotlight bubble — opt in to be considered for a feature and earn a few bonus points.",
          ] },
        ],
      },
    ],
  },
  {
    key: "notes",
    title: "Notes",
    blurb: "Jot down ideas, to-dos and checklists.",
    topics: [
      {
        id: "notes-overview",
        title: "Using Notes",
        desc: "Your private notebook, always a tap away.",
        article: [
          "Notes is your private space for ideas, reminders and to-do lists — only you can see your notes. Open it from the note icon at the top of your Home screen.",
          "Tap New note to start one. The first line becomes the title. Notes save automatically as you type, so you never need a Save button.",
          { heading: "Checklists", items: [
            "Tap \"Add checklist item\" inside a note to turn it into a to-do list.",
            "Tap the circle to check items off; checked items show with a line through them.",
          ] },
          { heading: "Staying organized", items: [
            "Pin important notes with the bookmark/pin button to keep them at the top.",
            "Use the search box to find any note by its title, text or list items.",
            "Pull down on the notes list to refresh.",
          ] },
        ],
      },
    ],
  },
  {
    key: "events",
    title: "Events",
    blurb: "Discover, RSVP and check in.",
    topics: [
      { title: "Browsing & RSVPing", desc: "Find events and save your spot.", tour: "feat_events" },
      {
        id: "past-events-hidden",
        title: "What happens after an event ends",
        desc: "Past events drop off automatically.",
        article: [
          "Once an event's end date and time have passed, it automatically drops off the member-facing Events page so you only ever see upcoming gatherings.",
          { heading: "Where past events go", items: [
            "Nothing is deleted — the event, its attendees and its details are all kept.",
            "If you RSVP'd, the event still appears in your own Tickets / My Events so you keep your record of it.",
            "The host (a Business or Brand Partner) can see all of their past events in the History tab of their Business Dashboard.",
            "Two Degrees admins can review every past event in Admin → Events → History.",
          ] },
          "Events that run across multiple days stay visible until their end time passes.",
        ],
      },
      {
        id: "virtual-events",
        title: "Virtual events & webinar links",
        desc: "Join online events from anywhere.",
        article: [
          "Two Degrees now supports Virtual events alongside in-person ones. When you're browsing Events, filter by the “Virtual” chip to see online-only gatherings.",
          "Virtual events don't have a street address. Instead, the host adds a meeting or webinar link (Zoom, Google Meet, Teams, and the like).",
          { heading: "Joining a virtual event", items: [
            "Open the event and RSVP as usual.",
            "On the event page you'll see a “Join online” button with the meeting link.",
            "The link opens in a new tab at the scheduled time, no travel required.",
          ] },
          { heading: "Hosting a virtual event (businesses)", items: [
            "In the Business Dashboard, create an event and choose the “Virtual” format.",
            "Paste your meeting or webinar link in the field that appears, the address fields are skipped for virtual events.",
            "Attendees get the link on the event page once they RSVP.",
          ] },
        ],
      },
    ],
  },
  {
    key: "community",
    title: "Community",
    blurb: "Join the conversation.",
    topics: [
      { title: "Posting & replying", desc: "Share and @mention people.", tour: "feat_community" },
      {
        id: "polls",
        title: "Polls",
        desc: "Vote and watch live results.",
        article: [
          "Polls are quick questions from the Two Degrees team. Some options include a photo, and pinned polls appear prominently (a poll pinned to the top always sits first).",
          { heading: "How voting works", items: [
            "Tap the option you want, you can vote once per poll.",
            "The moment you vote, the options turn into live result bars showing the percentage each option received, no separate results page.",
            "Come back anytime: once you've voted you'll always see the live ticker, which updates automatically.",
            "When a poll's end date passes (or an admin closes it), it shows final results only.",
          ] },
        ],
      },
      {
        id: "online-now",
        title: "Online now",
        desc: "See who's active in real time.",
        article: [
          "The Online now bubble shows a live count of members active right now, with a pulsing green dot so you can tell it's live.",
          { heading: "How it works", items: [
            "Find it in the app header (top bar on mobile, under your points in the sidebar on desktop).",
            "\"Online\" means a member has been active in the last 2 minutes.",
            "Tap the bubble to open the full list of who's online, each with their photo, name and membership tier. Tap anyone to open their profile.",
            "The count and list refresh automatically every 30 seconds, no reload needed.",
          ] },
        ],
      },
      {
        id: "community-statuses",
        title: "Statuses",
        desc: "Short same-day updates (Individual Plus).",
        article: [
          "A Status is a short update (up to 140 characters) you can share with the network, with an optional feeling tag.",
          { heading: "How to post one", items: [
            "Tap New post in Community and choose Status.",
            "Write up to 140 characters and optionally pick a feeling: Feeling motivated, Feeling curious, Open to connect, or Looking for referrals.",
            "Statuses show in the Community feed in a lighter card and can get replies and reactions.",
          ] },
          { heading: "Good to know", items: [
            "Statuses disappear at midnight (the end of the day you post them) and drop off the feed automatically, but they stay in your profile's Statuses tab with all their replies and reactions.",
            "Posting a Status is an Individual Plus feature. Individual Free members can view everyone's statuses but need to upgrade to post their own.",
          ] },
        ],
      },
      {
        id: "community-the-loop",
        title: "The Loop",
        desc: "An ephemeral photo feed of everyone's day (post with Plus).",
        article: [
          "The Loop is a live, ephemeral photo feed under Community. Members share single photos from their day, and everyone's posts appear together in one continuous chronological scroll.",
          { heading: "How it works", items: [
            "Post whenever you want — there's no prompt, no timer, and no limit on how many photos you share in a day.",
            "Each photo is a single shot with no caption. On mobile, tap Add to snap one or choose from your library; on the web, tap Add photo to upload one.",
            "Tap any photo to open the story-style viewer and swipe through everyone's day in the order it was posted.",
            "Posts automatically disappear at 11:59pm on the day they were posted (your local time) — The Loop always shows just today.",
          ] },
          { heading: "Good to know", items: [
            "Posting is an Individual Plus feature. Individual Free members can view The Loop and upgrade to post.",
            "You can delete your own posts anytime from the story viewer.",
          ] },
        ],
      },
    ],
  },
  {
    key: "network",
    title: "Networking",
    blurb: "Grow real connections.",
    topics: [
      { title: "Connecting & warm intros", desc: "Everyone's two degrees away.", tour: "feat_network" },
    ],
  },
  {
    key: "store",
    title: "The Store",
    blurb: "Shop official Two Degrees merch.",
    topics: [
      {
        id: "press-overview",
        title: "The Press, publishing & reading",
        desc: "Articles, newsletters and long-form posts.",
        article: [
          "The Press is the community's publishing platform. Open it from The Press in the sidebar. Every member, including Individual Free, can read the full Press feed and filter it by topic.",
          { heading: "Publishing (Individual Plus, Business & Brand Partner)", items: [
            "Tap Write to open the editor: add a title, optional subtitle and cover image, pick a category and tags.",
            "Use the rich editor for headings, bold, italic, bullet and numbered lists, quotes, links and images.",
            "Tap Check grammar to run a one-click spelling and grammar review. Each suggestion shows the fix; tap Apply on one, or Apply all to fix everything at once.",
            "Save a draft anytime, tap Preview to see the finished article, then Publish now or Schedule a future date and time.",
            "Manage everything under My articles, drafts, scheduled, and published pieces.",
          ] },
          { heading: "Following & subscribing", items: [
            "On any article, tap Subscribe to follow that publisher. You'll get an in-app notification (and an optional email) each time they publish.",
            "Switch the feed to Following (the toggle at the top of The Press) to see the latest articles only from publishers you follow, a quick reading digest.",
            "A member's published articles also appear under the Press tab on their profile.",
          ] },
          { heading: "Cross-posting", items: [
            "From Social Hub → The Press, pick a published article and share it to a connected platform (LinkedIn, X, Facebook, Instagram).",
            "The cross-post includes the title, a preview excerpt and a link back to the article inside Two Degrees. Connect accounts under Social Hub → Channels.",
          ] },
          "Individual Free members can read everything but see an upgrade prompt when they try to publish.",
        ],
      },
    ],
  },
  {
    key: "store",
    title: "The Store",
    blurb: "Shop official Two Degrees merch.",
    topics: [
      {
        id: "store-shopping",
        title: "Buying from the Store",
        desc: "Browse merch, pick your size, and check out.",
        article: [
          "The Store is where you can buy official Two Degrees merch. It's open to every member on any plan, including Individual Free.",
          { heading: "How to shop", items: [
            "Open Store from the sidebar (web) or the My 2° menu (mobile).",
            "Tap a product to see details, pick a variant (like size or color), choose a quantity, and Add to cart.",
            "Open your cart and tap Checkout. You'll be taken to Stripe's secure checkout to enter your shipping address and pay by card.",
            "Shipping and any applicable tax are calculated at checkout, so the total you see before paying is the final total.",
          ] },
          { heading: "After you order", items: [
            "You'll get an order confirmation on screen and by email.",
            "Track status under Store → My orders — it moves from Paid to Shipped once the team sends it.",
            "When your order ships you'll get an email with tracking details if provided.",
          ] },
        ],
      },
    ],
  },
  {
    key: "messaging",
    title: "Messaging",
    blurb: "Chat one-to-one.",
    topics: [
      { title: "Sending messages", desc: "DMs, photos and reactions.", tour: "feat_messages" },
    ],
  },
  {
    key: "profile",
    title: "Profile & verification",
    blurb: "Make your 2° shine.",
    topics: [
      { title: "Your My 2° page", desc: "Edit, share your QR, get verified.", tour: "feat_profile" },
    ],
  },
  {
    key: "opportunities",
    title: "Opportunities",
    blurb: "Roles & gigs from the community.",
    topics: [
      { title: "Browsing & applying", desc: "Find roles and apply fast.", tour: "feat_jobs" },
      {
        id: "opportunities-rename",
        title: "“Jobs” is now “Opportunities”",
        desc: "Same board, clearer name.",
        article: [
          "We renamed the Jobs board to Opportunities across the app, on the web menu, in the Business Dashboard, and in your matches. Nothing else changed: your saved roles, applications and matches all carry over.",
          "Look for “Opportunities” in the main navigation to browse roles and gigs shared by the network, and open “My 2°” to see your Opportunity matches.",
        ],
      },
    ],
  },
  {
    key: "membership",
    title: "Membership & Individual Plus",
    blurb: "Free vs Individual Plus, and how upgrades work.",
    topics: [
      {
        id: "trial-individual-plus",
        title: "Your 7-day Individual Plus free trial",
        desc: "Full Plus access for 7 days, no card needed.",
        article: [
          "Every new member starts with a free 7-day Individual Plus trial, no credit card required. You'll see a countdown so you always know how many days are left.",
          { heading: "What you get during the trial", items: [
            "Full Individual Plus access: Learning (LMS) courses and the Social Media Hub.",
            "Opportunities and Rewards.",
            "The Two Degrees Pay wallet and preferred matching for warmer introductions.",
          ] },
          { heading: "When the trial ends", items: [
            "After 7 days your account automatically moves to Individual Free, no charge and no action needed.",
            "Your data stays put; Plus-only features simply show an upgrade prompt again.",
            "Upgrade anytime from the Memberships page to keep Plus features without interruption.",
          ] },
          "If a Plus feature asks you to upgrade while your trial is still active, your access just needs a quick refresh, contact support and we'll sort it right away.",
        ],
      },
      {
        id: "membership-tiers",
        title: "Free vs Individual Plus features",
        desc: "What's included at each tier.",
        article: [
          "Two Degrees has a free tier plus paid Membership tiers (Individual Plus and Business) that unlock extra features.",
          { heading: "Always free", items: [
            "Home, Events, Community, Members, Messages and your profile.",
            "Connecting with people and joining conversations.",
          ] },
          { heading: "Unlocked with a paid Membership", items: [
            "Opportunities, Rewards, and preferred matching.",
            "Additional tools depending on your tier, shown on the Memberships page.",
          ] },
        ],
      },
      {
        id: "membership-preview",
        title: "Upgrading & the preview system",
        desc: "See what's behind a locked feature first.",
        article: [
          "Locked features aren't hidden, you can preview them before deciding to upgrade.",
          { heading: "How preview works", items: [
            "Open an Individual Plus feature and you'll see a live preview with an “Unlock with Individual Plus” prompt.",
            "This shows exactly what you'd get, so there are no surprises.",
          ] },
          { heading: "To upgrade", items: [
            "Open the Memberships page to compare tiers and what each includes.",
            "Choose a plan and complete checkout, your new features unlock immediately.",
          ] },
        ],
      },
      {
        id: "membership-all-tiers",
        title: "What each plan unlocks (all four tiers)",
        desc: "Individual Free, Individual Plus, Business & Brand Partner.",
        article: [
          "Two Degrees has four membership tiers. Here's exactly what each one can access, so you know what an upgrade unlocks.",
          { heading: "Individual Free", items: [
            "Home, Events, Community, Members and Messages.",
            "Your profile, connecting with people, and joining conversations.",
            "Locked features aren't hidden, you'll see a friendly preview with an upgrade prompt.",
          ] },
          { heading: "Individual Plus", items: [
            "Everything in Free, plus Opportunities and Rewards.",
            "The Two Degrees Pay wallet (load, pay and withdraw).",
            "Learning (LMS) courses and the Social Media Hub.",
            "Preferred matching for warmer introductions.",
          ] },
          { heading: "Business", items: [
            "Everything in Individual Plus.",
            "The Business Dashboard and the full Business Suite: Analytics, Marketing Hub, Projects, Docs, Calendar, HR, CRM, Accounting, Contracts, Support, Recruiting, Inventory, Payroll and Expenses.",
            "Team Chat for your organization.",
          ] },
          { heading: "Brand Partner", items: [
            "Everything a Business account can access.",
            "Partner-level placement and branding across the community.",
          ] },
          "Platform admins at Two Degrees always see every feature, regardless of tier.",
        ],
      },
    ],
  },
  {
    key: "wallet",
    title: "Two Degrees Pay (Wallet)",
    blurb: "Load, pay and withdraw, Individual Plus and up.",
    topics: [
      { title: "Using your wallet", desc: "Load a balance and pay in a tap.", tour: "feat_wallet" },
      {
        id: "wallet-guide",
        title: "Loading, spending & withdrawing",
        desc: "A step-by-step walkthrough.",
        article: [
          "Two Degrees Pay is your in-app wallet. It's available to active Individual Plus members and above; if your membership lapses, your balance is safely frozen until you upgrade again.",
          { heading: "1. Activate your wallet", items: [
            "Open Two Degrees Pay from the menu.",
            "Complete a quick identity verification (handled securely by Stripe Identity), this is a one-time step before you can hold a balance.",
          ] },
          { heading: "2. Load funds", items: [
            "Tap “Add funds” and choose an amount.",
            "Pay with a debit card or bank transfer (processed by Stripe).",
            "Turn on auto-reload so your balance tops up automatically when it runs low.",
          ] },
          { heading: "3. Pay & transfer", items: [
            "Show your rotating pay QR to a business to pay at an event or settle an invoice.",
            "Send a balance to another member directly from your wallet.",
          ] },
          { heading: "4. Withdraw", items: [
            "Request a withdrawal to your verified bank account.",
            "Funds typically arrive in 3 to 5 business days.",
          ] },
          "Your full transaction history lives in the wallet for your records.",
        ],
      },
    ],
  },
  {
    key: "business-suite",
    title: "Business Suite modules",
    blurb: "Analytics, Marketing, Social, Learning & Team Chat.",
    topics: [
      {
        id: "suite-analytics",
        title: "Analytics Dashboard",
        desc: "Business & Brand Partner, on web.",
        article: [
          "The Analytics Dashboard gives you a deep view of performance: revenue, registrations, check-ins, conversion, no-shows and member growth.",
          "Availability: Business and Brand Partner accounts, on the web app.",
          { heading: "Getting around", items: [
            "Switch between the Platform, Docs, Calendar, Contracts, Support, Marketing, Recruiting and Inventory tabs.",
            "Choose a range (7, 30, 90 days, all time, or a custom window) and toggle “vs previous period” to see trends.",
            "Pin the metrics you care about, then tap any stat to drill into the records behind it.",
            "Export to CSV or PDF, or schedule a report to be emailed on a cadence.",
          ] },
        ],
      },
      {
        id: "suite-marketing",
        title: "Marketing Hub",
        desc: "Business & Brand Partner, on web.",
        article: [
          "The Marketing Hub reaches the people connected to your business, never the whole platform.",
          "Availability: Business and Brand Partner accounts, on the web app.",
          { heading: "What you can do", items: [
            "See an overview of active campaigns, subscribers and open rates.",
            "Build email campaigns with a guided wizard (Audience, Content, Schedule, Review) and a block editor (heading, text, image, button, divider).",
            "Preview on desktop or mobile before you send, then send now or schedule.",
            "Publish banners and manage your social posts, calendar and send history from one place.",
          ] },
        ],
      },
      {
        id: "suite-social",
        title: "Social Media Hub",
        desc: "Individual Plus and up, web & mobile.",
        article: [
          "The Social Media Hub helps you plan, schedule and track posts across your connected channels.",
          "Availability: Individual Plus, Business and Brand Partner, on both web and mobile.",
          { heading: "Getting started", items: [
            "Connect your first channel from the Channels tab (Facebook, Instagram, LinkedIn, TikTok, X).",
            "Use Compose to write a post, pick channels, and see a live preview per platform.",
            "Check the “Best time to post” suggestions, drawn from your recent engagement, and schedule with a tap.",
            "Track impressions, clicks and shares in the 7-day engagement view.",
          ] },
        ],
      },
      {
        id: "suite-lms",
        title: "Learning (LMS)",
        desc: "Individual Plus and up, web & mobile.",
        article: [
          "Learning is your library of courses and lessons on the platform.",
          "Availability: Individual Plus, Business and Brand Partner, on both web and mobile.",
          { heading: "How it works", items: [
            "Open Learning to browse the available courses.",
            "Work through lessons at your own pace, your progress is saved as you go.",
          ] },
        ],
      },
      {
        id: "suite-teamchat",
        title: "Team Chat",
        desc: "Business and up, web & mobile.",
        article: [
          "Team Chat is a real-time workspace for your organization, with channels and direct messages.",
          "Availability: Business and Brand Partner accounts, on both web and mobile.",
          { heading: "Using Team Chat", items: [
            "Create channels for topics or projects, and send direct messages to teammates.",
            "Invite guests, search messages, and switch between a light or dark theme.",
            "Share files and keep every conversation in one organized place.",
          ] },
        ],
      },
      { title: "Take a Social Media Hub tour", desc: "Walk through it live on your screen.", tour: "feat_social" },
      { title: "Take a Learning tour", desc: "Walk through it live on your screen.", tour: "feat_learning" },
      { title: "Take a Team Chat tour", desc: "Walk through it live on your screen.", tour: "feat_teamchat" },
    ],
  },
  {
    key: "recruiting",
    title: "Recruiting & ATS",
    blurb: "Hire with an applicant tracking system.",
    topics: [
      {
        id: "recruiting-guide",
        title: "Running your hiring pipeline",
        desc: "Jobs, applicants, interviews & offers.",
        article: [
          "Recruiting is a full applicant tracking system (ATS) in the Admin Dashboard, for Business accounts and your hiring team.",
          { heading: "1. Post a job", items: [
            "Open Admin Dashboard → Recruiting and create a job (title, department, description).",
            "Each job gets its own public apply link you can share; candidates apply with a resume.",
            "Set the job to Draft, Open or Closed at any time.",
          ] },
          { heading: "2. Track applicants", items: [
            "Applicants land on a Kanban board with stages: Applied, Phone Screen, Interview, Offer, Hired, Rejected (customizable).",
            "Drag a candidate between stages as they progress.",
            "Open the applicant drawer to read the cover letter, download the resume, and add notes, tags and scorecards.",
          ] },
          { heading: "3. Interviews & offers", items: [
            "Schedule an interview with an interviewer, it adds a calendar event and emails the candidate a confirmation.",
            "Make an offer to hire: this creates an HR employee record, sends an offer-letter contract to e-sign, and moves them to Hired.",
          ] },
          { heading: "4. Measure", items: [
            "The Recruiting tab in Analytics shows open roles, applicants, hires, offer-acceptance rate, time-to-fill and candidate sources.",
          ] },
        ],
      },
    ],
  },
  {
    key: "admin-controls",
    title: "Admin feature controls",
    blurb: "For platform admins.",
    topics: [
      {
        id: "admin-feature-launch",
        title: "Feature launches & tutorials",
        desc: "Announce new features and set their first-time walkthroughs.",
        article: [
          "Every feature in Feature Controls has an 'Edit launch message & tutorial' link next to its toggle where you set three things: a one-line Description, a Location hint (where to find it), and a deep link.",
          { heading: "Launch announcement", items: [
            "When you switch a feature from Off or Coming Soon to On, every approved, active member who's eligible for it gets an in-app notification AND an email with the description and where to find it.",
            "Pending, rejected and waitlisted members are never notified.",
            "Toggle 'Announce to eligible members' off to activate the feature silently with no notification or email.",
          ] },
          { heading: "First-time tutorial", items: [
            "Add 3–5 steps (title + explanation). The first time an eligible member opens the feature, a skippable walkthrough overlay appears.",
            "It shows only once per member per feature; members can skip at any step.",
            "Edit the steps anytime — members who haven't seen it yet get the latest version.",
          ] },
        ],
      },
      {
        id: "admin-polls",
        title: "Polls",
        desc: "Create polls, pin to Home, close & see results.",
        article: [
          "Create a poll from Admin Dashboard → Polls: add a question and 2-6 answer options (each option can optionally include a photo), an optional end date, and optionally pin it. \"Pin to Home feed\" surfaces it on every member's Home; \"Pin to top of page\" locks it to the very top of the Polls list above all other polls.",
          { heading: "Managing polls", items: [
            "Add a photo to any answer option when creating the poll — members see it while voting and in the live results.",
            "Members vote once; results update live as votes come in.",
            "Pin to Home, or pin to the top of the page, independently — toggle either from each poll's controls.",
            "Close a poll to lock in final results (or reopen it), or delete it entirely.",
            "Turn the whole feature on or off platform-wide in Feature Controls → Community → Polls.",
          ] },
        ],
      },
      {
        id: "admin-online-now",
        title: "Online now presence",
        desc: "Toggle the live 'who's online' bubble for members.",
        article: [
          "The Online now bubble shows every member a live count of who's active (within the last 2 minutes), refreshing every 30 seconds.",
          "Turn it on or off platform-wide from Admin Dashboard → Feature Controls → Community → Online Now presence. When off, the bubble disappears for all members and the presence API is disabled.",
          "Admins also get a richer Online Now KPI with drill-down in the Admin Overview.",
        ],
      },
      {
        id: "admin-analytics-dashboard",
        title: "Analytics dashboard",
        desc: "Growth, engagement, events, revenue & operations — every number is clickable.",
        article: [
          "The Analytics section (Admin Dashboard → Analytics) gives a full read on the platform across five areas: Growth, Engagement, Events, Revenue & retention, and Operations. Every metric card is clickable and opens a drill-down showing the exact members or records behind the number, which you can export to CSV.",
          { heading: "What's covered", items: [
            "Growth: total members, tier breakdown, new members (daily/weekly/monthly), signup source (from UTM links), the waitlist→application→approved→paid funnel, and tier upgrades/downgrades.",
            "Engagement: weekly/monthly active members, new connections, asks posted and % answered, sessions and average length, and messages sent.",
            "Events: registrations, check-ins, no-show rate, events created per month, and top events.",
            "Revenue & retention: MRR by tier, churn, cancellation reasons, and failed payments.",
            "Operations: average application review time, content-safety reports and status, crash events, and onboarding drop-off.",
          ] },
          { heading: "Good to know", items: [
            "Use the date-range control to filter every chart; compare against the previous period with one tap.",
            "Some streams (signup source, sessions, crashes, onboarding, cancellation reasons) are captured going forward and show a 'collecting since' date until history builds up.",
            "Turn the whole section on or off with the Analytics ON/OFF toggle in the Analytics header (admins only).",
          ] },
        ],
      },
      {
        id: "admin-status-config",
        title: "Status feelings & reactions",
        desc: "Edit the feelings, reaction emojis and status length.",
        article: [
          "Member statuses let people post a short update with an optional feeling. You control the list of feelings, the emoji reactions, and the character limit from Admin Dashboard → Settings → Status feelings & reactions.",
          { heading: "What you can edit", items: [
            "Feelings — add, rename or remove any feeling. Each needs an emoji and a label (e.g. 🙂 \"Feeling optimistic\"). A short key is generated automatically from the label.",
            "Reaction emojis — the quick reactions members can tap on a status. Add any emoji; remove ones you don't want.",
            "Character limit — the maximum length of a status (between 40 and 500 characters).",
          ] },
          "Changes save instantly and apply to both the web and mobile apps with no redeploy. Use Reset to defaults to restore the original feelings, reactions and 140-character limit.",
        ],
      },
      {
        id: "admin-announcement-banner",
        title: "Announcement banner",
        desc: "Show a dismissible heads-up to every member.",
        article: [
          "The announcement banner shows a site-wide message to every member at the top of each page, on both web and mobile. The highlighted phrase in the message is a tappable link.",
          { heading: "Editing & controlling it", items: [
            "Go to Admin Dashboard → Settings → Announcement banner.",
            "Use Turn banner ON / OFF. When ON, every member sees it until they dismiss it; when OFF, it disappears for everyone.",
            "Edit the Message, the Link phrase (the words that become the link — they must appear in the message), and where the link opens (the in-app Help section or a custom URL). A live preview shows how it will look.",
            "Saving a new message makes the banner reappear even for members who already dismissed the old one.",
            "Each member can dismiss the banner with the X, and it won't come back for them until the message changes.",
          ] },
        ],
      },
      {
        id: "admin-events-history",
        title: "Event history & auto-hiding",
        desc: "Past events leave the member page but stay on record.",
        article: [
          "When an event's end date and time pass, it's automatically removed from the member-facing Events page — members only ever see upcoming events. No event data is deleted.",
          { heading: "Where to review past events", items: [
            "Go to Admin Dashboard → Events and open the History tab to see every event that has ended, across all hosts.",
            "Each row keeps its attendees, RSVPs and check-in records intact.",
            "Businesses and Brand Partners see only their own past events, under the History tab of their Business Dashboard → Events.",
          ] },
          "Multi-day events stay on the member page until their end time passes.",
        ],
      },
      {
        id: "admin-press",
        title: "Managing The Press",
        desc: "Toggle it, feature articles, and moderate content.",
        article: [
          "The Press is a toggleable feature: turn it on or off (per plan / account type) from Admin Dashboard → Feature Controls, under Community. The 'The Press — Following feed' toggle (same panel) separately controls the Discover/Following reading digest; turn it off to show Discover only.",
          { heading: "Moderation & curation", items: [
            "Open any article to Feature it, this pins it to the top of the Press discovery feed. Tap again to unfeature.",
            "Super Admins can Unpublish (back to the author's drafts) or Remove any article from the article page. Removing hides it everywhere without deleting the author's data.",
          ] },
          "Publishing is limited to Individual Plus, Business and Brand Partner members; Individual Free members can read the feed only.",
        ],
      },
      {
        id: "admin-store",
        title: "Managing The Store",
        desc: "Add merch, manage stock and fulfill orders.",
        article: [
          "The Store is managed from Admin Dashboard → Store. It has two tabs: Orders and Products, plus a stats strip (paid orders, revenue, orders to ship, and low-stock items).",
          { heading: "Products", items: [
            "Tap New product to add merch: title, description, category, images, and one or more variants (size/color).",
            "Each variant has its own price and stock, and is automatically tracked as a real SKU in the Inventory module — sales decrement inventory and trigger the usual low-stock alerts and restock tasks.",
            "For a single-option product, just leave the variant label blank.",
            "Edit or remove products anytime. Removing a product hides it from the storefront and archives its inventory SKUs.",
          ] },
          { heading: "Orders & fulfillment", items: [
            "The Orders tab defaults to 'To ship' — paid orders awaiting fulfillment.",
            "Open an order to see the items and the customer's shipping address.",
            "Tap Mark shipped, enter the carrier and tracking number, and the customer is emailed automatically.",
          ] },
          "Checkout runs through Stripe with shipping-address collection and automatic tax. The Store is available to all members on every plan.",
        ],
      },
      {
        id: "admin-loop",
        title: "Managing The Loop",
        desc: "Moderate members' ephemeral Loop photos.",
        article: [
          "The Loop is now a simple ephemeral photo feed — there's no prompt, schedule, or posting window to manage. Members post single photos that automatically disappear at 11:59pm their local time.",
          { heading: "Moderation", items: [
            "Open Admin Dashboard → The Loop to see every active (not-yet-expired) post as a grid, newest first.",
            "Tap a photo to view it full-size; tap the trash icon to remove anything inappropriate immediately.",
            "To turn the whole feature on or off for members, use Feature Controls → Community → The Loop.",
          ] },
        ],
      },
      {
        id: "admin-feature-toggles",
        title: "Feature toggle controls",
        desc: "Turn features on/off at three levels.",
        article: [
          "Platform admins can switch features on or off from the Admin Dashboard → Feature Controls. Toggles apply on a strict three-level hierarchy.",
          { heading: "The three levels (most specific wins)", items: [
            "Individual member override, set for one person; beats everything else.",
            "Account-type override, set for all Free, Individual Plus or Business accounts.",
            "Platform default, the app-wide on/off switch.",
          ] },
          "So an individual override always beats an account-type override, which beats the platform default. This lets you pilot a feature with one member, roll it out to a whole tier, or flip it for everyone.",
        ],
      },
      {
        id: "admin-feature-controls-panel",
        title: "The Feature Controls panel",
        desc: "Toggle every module, grouped and tracked.",
        article: [
          "The Feature Controls panel (Admin Dashboard → Feature Controls) lets you turn any feature module on or off for your members.",
          { heading: "What you'll see", items: [
            "Every module, organized into four groups: Core Platform, Business Suite, Community and Admin Tools.",
            "A clear on/off state for each module, with a count of how many are on in each group.",
            "A “Last changed” line showing when each toggle was last updated and by which admin.",
          ] },
          { heading: "How toggles behave", items: [
            "Turn a module off and it immediately disappears from the menu and becomes inaccessible for affected members.",
            "Tier-gated features (like Social, Learning and Team Chat) still respect the member's plan on top of these toggles.",
          ] },
          "Admins are always exempt, you keep full access to every feature even when a module is switched off for members.",
        ],
      },
      {
        id: "admin-member-journey",
        title: "Member Journey",
        desc: "The signup-to-paid flow, at a glance.",
        article: [
          "Admin Dashboard → Member Journey is a read-only reference showing exactly what a new member sees, step by step, from first visit to paid conversion.",
          { heading: "The 11 steps", items: [
            "Landing / Sign Up → Registration (no card required) → Onboarding (interests, profile, welcome tour).",
            "Home Feed with the 7-day trial banner → Events → Community / Within Two Degrees → Opportunities → Comms Workspace.",
            "Trial Expiry prompt on day 7 → Stripe Checkout → Full Plus access confirmed.",
          ] },
          "Cards marked with a “Plus” badge surface Individual Plus features, unlocked during the 7-day trial and gated behind checkout once it ends. It's a reference only, there's nothing to configure here.",
        ],
      },
      {
        id: "admin-platform-settings",
        title: "Platform Settings",
        desc: "Contact info, email sandbox & Plus safeguards.",
        article: [
          "Platform admins manage global settings from Admin Dashboard → Settings.",
          { heading: "Contact info", items: [
            "Set the support email, phone and company address once; they populate the Support Center, Legal pages and account screens across web and mobile.",
          ] },
          { heading: "Email sandbox mode", items: [
            "A safety switch for Email Marketing. While ON (the default), every campaign and automation email is redirected to your support inbox instead of real recipients, with a [SANDBOX] subject prefix.",
            "A clear warning appears on the campaign send screen whenever sandbox is on.",
            "Switch to live mode only when you're ready to email real members.",
          ] },
          { heading: "Plus modules safeguard", items: [
            "“Reset Plus modules to live” forces Learning and the Social Media Hub back on for every Plus-entitled member (including free-trial users), in case a stray toggle gated them. Individual Free members are unaffected.",
          ] },
          { heading: "Employee Status", items: [
            "Mark any member as an Employee from their member file (Admin → Members → open a member) or the CRM. Employees are automatically excluded from the Rewards program: they earn no points, can't redeem rewards, and are hidden from leaderboards.",
            "Their existing points are frozen (not deleted) and restored instantly the moment you turn the Employee toggle off.",
            "Works independently of membership tier. A global “Employee Status” switch in Admin → Feature Controls turns the whole behaviour on (default) or off platform-wide.",
            "A clear Employee badge shows on the member's profile on web and mobile.",
          ] },
        ],
      },
      {
        id: "admin-email-marketing",
        title: "Email Marketing safeguards",
        desc: "Approval, separation of duties & preview.",
        article: [
          "The platform Email Marketing module (Admin Dashboard → Marketing → Email tab) has guardrails to prevent mistakes.",
          { heading: "Approval before send", items: [
            "A campaign must be approved before it can be sent.",
            "Separation of duties: the admin who created a campaign can't approve their own, a different admin must approve it.",
            "Approvals and sends are recorded in the audit log.",
          ] },
          { heading: "Preview to yourself", items: [
            "Use “Preview to me” on any campaign to send its exact rendered email to your own inbox before going live.",
          ] },
          { heading: "Sandbox protection", items: [
            "When email sandbox mode is on (Settings), all sends are redirected to your support inbox, so test campaigns never reach members.",
          ] },
        ],
      },
      {
        id: "admin-publishing-updates",
        title: "Publishing an update",
        desc: "The 60-second heads-up banner & re-login flow.",
        article: [
          "When you publish a new version (Admin Dashboard → Settings → “Publish new version”), every currently logged-in member on web and mobile is given a heads-up before anything changes.",
          { heading: "What members see", items: [
            "A dismissible banner appears at the top of the app reading: “A new update is being applied. You may be logged out shortly.” with a live 60-second countdown.",
            "Members can dismiss the banner; it does not interrupt what they're doing and no one is force-logged-out.",
          ] },
          { heading: "After the update", items: [
            "Once a member next needs to sign in again, the login screen greets them with “We just updated the app. Please log back in.” so it's clear why they're back at login.",
          ] },
          "This only controls the user-facing notice. It does not change or trigger the deployment itself, run your deploy as usual and the banner keeps members informed.",
        ],
      },

      {
        id: "admin-roles",
        title: "Admin roles & permissions",
        desc: "The role hierarchy and the View/Manage matrix.",
        article: [
          "Admin access is controlled by roles, managed by the Super Admin under Admin Dashboard → Team.",
          { heading: "The hierarchy", items: [
            "Super Admin, full, unrestricted access to everything. This role can't be edited or limited, and is the only role allowed to create, edit, delete and assign roles.",
            "Organization Admin, Community Manager, Event Host, Analyst and Support, all fully editable. By default Organization Admin has full access; the others are scoped to their day-to-day work.",
          ] },
          { heading: "View / Manage matrix", items: [
            "Open a role to see a matrix with one row per admin section (Members, CRM, Events, Rewards, Feature Controls, Comms/Messaging, Analytics, Billing, Member Journey) and two toggles: View (can open the section) and Manage (can make changes). Turning on Manage automatically grants View.",
            "All permissions are enforced on the backend, so a role can never do more than its toggles allow, even via the API.",
          ] },
          { heading: "Assigning roles", items: [
            "The Super Admin assigns a role to any admin from that member's file (Admin → Members → open member → Admin role), or from the Team panel.",
          ] },
        ],
      },
    ],
  },
];

// Help Center categories for BUSINESS users (in the console).
export const HELP_BUSINESS = [
  {
    key: "biz-start",
    title: "Getting started",
    blurb: "A tour of your dashboard.",
    topics: [
      { title: "Replay the dashboard tour", desc: "The full walkthrough.", tour: BUSINESS_WELCOME_KEY },
    ],
  },
  {
    key: "biz-tools",
    title: "Business tools & team access",
    blurb: "Run your own tools and control who can use them.",
    topics: [
      { title: "Tools / Setup walkthrough", desc: "Grant teammates access to each tool.", tour: "feat_biz_tools" },
      {
        id: "biz-tools-access",
        title: "How business tools & team access work",
        desc: "Each tool is private to your business.",
        article: [
          "Your business runs its own instance of every tool — CRM, Social & Email Marketing, Projects, Docs & Wiki, Contracts & E-Signatures, Team, HR, Payroll, Recruiting, Business Finance, Accounting and Expenses. Your data stays private to your business, separate from Two Degrees and other accounts.",
          { heading: "Granting access to your team", items: [
            "Open Business Dashboard → Tools / Setup.",
            "For each tool, tap a teammate's name to grant or revoke their access.",
            "Owners and managers always have access to every tool; staff only see the tools you've granted them.",
            "A teammate's left-hand menu automatically shows just the tools they can use — under “Grow my business” and “Business tools”.",
          ] },
          "You can change access anytime — updates take effect immediately.",
        ],
      },
    ],
  },
  {
    key: "biz-events",
    title: "Events",
    blurb: "Create and run events.",
    topics: [
      { title: "Creating an event", desc: "Publish, manage RSVPs & check-in.", tour: "feat_biz_events" },
      {
        id: "biz-event-history",
        title: "Past events & History",
        desc: "Find events you've already hosted.",
        article: [
          "After an event ends, it automatically leaves the member-facing Events page so members only see what's coming up — but you keep full access to it.",
          { heading: "Your History tab", items: [
            "Open Business Dashboard → Events and switch from Upcoming to History.",
            "History lists every event you've hosted that has ended, with its attendees, RSVPs and check-in records intact.",
            "Only you (and Two Degrees admins) can see your past events here — they're hidden from the public Events page.",
          ] },
          "Multi-day events stay under Upcoming until their end time passes.",
        ],
      },
    ],
  },
  {
    key: "biz-pos",
    title: "Invoicing & POS",
    blurb: "Bill members with ease.",
    topics: [
      { title: "Building an invoice", desc: "Line items, tax and pay QR.", tour: "feat_biz_pos" },
      {
        id: "invoice-branding",
        title: "Brand your invoices & resend",
        desc: "Add your logo, color and details; resend anytime.",
        article: [
          "In the Accounting → Invoicing tab, tap Branding to make invoices look like your company's: upload your logo, pick a brand color, and add your business name, email and address.",
          "Your branding appears on the invoice PDF, the public pay-link page, and the email your client receives.",
          "When you Send an invoice that has a client email, we automatically email the client a branded summary with a Pay now button; replies go to your business email. Use the Resend button (circular-arrow icon) on any sent or overdue invoice to email it again - the pay link stays the same.",
        ],
      },
    ],
  },
  {
    key: "biz-scan",
    title: "QR scanning & payments",
    blurb: "Take payments on the spot.",
    topics: [
      { title: "Scanning to charge", desc: "Charge a member's pay code.", tour: "feat_biz_scan" },
    ],
  },
  {
    key: "biz-analytics",
    title: "Analytics",
    blurb: "Understand your performance.",
    topics: [
      { title: "Reading your analytics", desc: "Views, RSVPs, attendance, sales.", tour: "feat_biz_analytics" },
    ],
  },
  {
    key: "biz-catalog",
    title: "Product catalog",
    blurb: "Your products & services.",
    topics: [
      { title: "Setting up your catalog", desc: "Add items ready to invoice.", tour: "feat_biz_catalog" },
    ],
  },
  {
    key: "biz-virtual",
    title: "Virtual events",
    blurb: "Host online, with a webinar link.",
    topics: [
      {
        id: "biz-virtual-events",
        title: "Hosting a virtual event",
        desc: "Add a meeting or webinar link.",
        article: [
          "When creating an event, choose the “Virtual” format to host online.",
          { heading: "Setting it up", items: [
            "Pick “Virtual” as the event format, the street-address fields are replaced by a single link field.",
            "Paste your meeting or webinar link (Zoom, Google Meet, Teams, etc.).",
            "Attendees see a “Join online” button on the event page once they RSVP.",
          ] },
        ],
      },
    ],
  },
  {
    key: "biz-crm",
    title: "CRM",
    blurb: "Your own contacts & pipeline.",
    topics: [
      {
        id: "biz-crm-basics",
        title: "Managing contacts & pipeline",
        desc: "Leads, stages, reports & integrations.",
        article: [
          "The CRM tab is your own private pipeline, isolated to your business. No one else can see your contacts.",
          { heading: "What's inside", items: [
            "Contacts: add, edit and track leads with notes and a full activity timeline.",
            "Pipeline: drag contacts between stages; customize the stages to match how you sell.",
            "Reports: see totals, pipeline value, win rate and where leads come from.",
            "Integrations: connect your own Mailchimp, HubSpot, Zendesk or Freshdesk account and test the connection.",
          ] },
          { heading: "Giving your team access", items: [
            "Only the owner and managers see the CRM by default.",
            "To let a team member in, grant them the “Manage CRM” permission in the Team tab.",
          ] },
        ],
      },
    ],
  },
  {
    key: "biz-marketing",
    title: "Marketing Hub",
    blurb: "Reach your own audience.",
    topics: [
      {
        id: "biz-marketing-basics",
        title: "Email, push, banners & social",
        desc: "Campaigns to your own network.",
        article: [
          "The Marketing Hub lets you reach the people connected to your business, never the whole platform.",
          { heading: "Your audience", items: [
            "Pick who to reach: event attendees, your team, your followers/connections, and your CRM contacts.",
            "A live recipient count updates as you toggle each group.",
          ] },
          { heading: "Channels", items: [
            "Social: post to your connected channels (see the Social Media Hub).",
            "Email & Push: compose a message, choose your audience, then send now or schedule it.",
            "Banner: publish a banner that appears on your business profile and your event pages.",
            "Calendar & History track what's scheduled and what's already gone out.",
          ] },
          "Access is controlled by the owner via the “Manage marketing” permission in the Team tab.",
        ],
      },
    ],
  },
];
